import React, { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import HoverTooltip from "../../components/common/HoverTooltip";
import * as Yup from "yup";
import { Pencil } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../../components/common/Translations";
import CommonButton from "../../components/common/CommonButton";
import { Spinner } from "../../Spinner";
import { toast } from "../../utils/toast";
import { getPermissionByScreenCode } from "../../utils/permissions";
import {
  getAppUpdateConfig,
  updateAppUpdateConfig,
} from "../../service/AppUpdateConfig";

const PLATFORMS = [
  { key: "android", label: "Android" },
  { key: "ios", label: "iOS", disabled: true },
];

const normalizeConfig = (data, platform) => {
  if (!data) return null;
  let value = data?.body?.value ?? data?.data ?? data;
  if (Array.isArray(value)) {
    value = value.find((item) => item?.platform === platform) || value[0];
  }
  return value || null;
};

const emptyConfig = (platform) => ({
  platform,
  latestVersion: "",
  latestBuildNumber: "",
  minimumSupportedBuildNumber: "",
  updateUrl: "",
  title: "",
  message: "",
  forceUpdate: false,
  releaseNotes: [],
  updatedAt: "",
});

const buildInitialValues = (config, platform) => ({
  platform: config?.platform || platform,
  latestVersion: config?.latestVersion ?? "",
  latestBuildNumber: config?.latestBuildNumber ?? "",
  minimumSupportedBuildNumber: config?.minimumSupportedBuildNumber ?? "",
  updateUrl: config?.updateUrl ?? "",
  title: config?.title ?? "",
  message: config?.message ?? "",
  forceUpdate: config?.forceUpdate ?? false,
  releaseNotesText: (config?.releaseNotes || []).join("\n"),
});

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const AppUpdateConfig = () => {
  const { theme, langMode } = useContext(AppContext);
  const permission = getPermissionByScreenCode("APP_UPDATE_CONFIG");
  const canUpdate = permission?.canUpdate ?? false;

  const [activePlatform, setActivePlatform] = useState(PLATFORMS[0].key);
  const [configs, setConfigs] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const themeClass =
    theme === "dark"
      ? "dark-mode"
      : theme === "high-contrast"
        ? "high-contrast"
        : "";

  const activeConfig = configs[activePlatform];

  const fetchConfig = async (platform) => {
    setLoading(true);
    try {
      const response = await getAppUpdateConfig(platform);
      const normalized = normalizeConfig(response, platform);
      // Always fall back to platform-specific defaults so the Platform field is
      // populated from the active tab even when the DB has no (or partial) data.
      const config = { ...emptyConfig(platform), ...(normalized || {}), platform };
      setConfigs((prev) => ({ ...prev, [platform]: config }));
    } catch (error) {
      toast.error(
        error.message || translations[langMode].appUpdateConfigLoadError
      );
      setConfigs((prev) => ({ ...prev, [platform]: emptyConfig(platform) }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load whichever platform tab is active and not yet fetched.
    if (!configs[activePlatform]) {
      fetchConfig(activePlatform);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlatform]);

  const requiredMsg = translations[langMode].appUpdateFieldRequired;
  const numberMsg = translations[langMode].appUpdateNumberInvalid;
  const negativeMsg = translations[langMode].appUpdateNumberNegative;

  const validationSchema = Yup.object().shape({
    latestVersion: Yup.string().trim().required(requiredMsg),
    latestBuildNumber: Yup.number()
      .transform((value, original) =>
        String(original).trim() === "" ? undefined : value
      )
      .typeError(numberMsg)
      .min(0, negativeMsg)
      .required(requiredMsg),
    minimumSupportedBuildNumber: Yup.number()
      .transform((value, original) =>
        String(original).trim() === "" ? undefined : value
      )
      .typeError(numberMsg)
      .min(0, negativeMsg)
      .required(requiredMsg),
    updateUrl: Yup.string().trim().required(requiredMsg),
    title: Yup.string().trim().required(requiredMsg),
    message: Yup.string().trim().required(requiredMsg),
    releaseNotesText: Yup.string().test(
      "has-release-note",
      translations[langMode].appUpdateReleaseNotesRequired,
      (value) => (value || "").split("\n").some((note) => note.trim())
    ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: buildInitialValues(activeConfig, activePlatform),
    validationSchema,
    onSubmit: async (values) => {
      const releaseNotes = values.releaseNotesText
        .split("\n")
        .map((note) => note.trim())
        .filter(Boolean);

      const payload = {
        platform: values.platform,
        latestVersion: values.latestVersion.trim(),
        latestBuildNumber: Number(values.latestBuildNumber),
        minimumSupportedBuildNumber: Number(values.minimumSupportedBuildNumber),
        updateUrl: values.updateUrl.trim(),
        title: values.title.trim(),
        message: values.message.trim(),
        forceUpdate: values.forceUpdate,
        releaseNotes,
      };

      setSaving(true);
      try {
        const response = await updateAppUpdateConfig(payload);
        const normalized = normalizeConfig(response, values.platform);
        const updated = {
          ...emptyConfig(values.platform),
          ...(normalized || { ...activeConfig, ...payload }),
          platform: values.platform,
        };
        setConfigs((prev) => ({ ...prev, [values.platform]: updated }));
        setIsEditing(false);
        toast.success(translations[langMode].appUpdateConfigUpdateSuccess);
      } catch (error) {
        toast.error(
          error.message || translations[langMode].appUpdateConfigUpdateError
        );
      } finally {
        setSaving(false);
      }
    },
  });

  const handleTabChange = (platform) => {
    if (platform === activePlatform) return;
    if (isEditing) return;
    setActivePlatform(platform);
  };

  const handleEdit = () => {
    formik.resetForm({ values: buildInitialValues(activeConfig, activePlatform) });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    formik.resetForm({ values: buildInitialValues(activeConfig, activePlatform) });
  };

  const inputClass = `block p-3 w-full text-sm text-gray-900 bg-transparent rounded-md border border-[#D1D5DB] appearance-none focus:outline-none focus:ring-0 focus:border-gray-500 peer ${themeClass}`;
  const labelClass = "text-sm flex items-center gap-2";
  const valueClass =
    "block p-3 w-full min-h-[46px] text-sm font-medium text-gray-900 bg-gray-50 rounded-md border border-[#E5E7EB] break-words";

  const renderField = (
    label,
    field,
    { type = "text", readOnly = false, required = false, fallback } = {}
  ) => {
    const editable = isEditing && !readOnly;
    const error = formik.touched[field] && formik.errors[field];
    return (
      <div className="flex flex-col gap-2">
        <label className={labelClass}>
          <span>{label}</span>
          {editable && required && <span className="text-[#FF9F29]">*</span>}
        </label>
        {editable ? (
          <>
            <input
              type={type}
              name={field}
              min={type === "number" ? 0 : undefined}
              className={`${inputClass} ${error ? "border-red-500" : ""}`}
              value={formik.values[field] ?? ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onKeyDown={
                type === "number"
                  ? (e) => {
                      if (["-", "+", "e", "E"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }
                  : undefined
              }
            />
            {error && (
              <p className="text-xs text-red-500">{formik.errors[field]}</p>
            )}
          </>
        ) : (
          <p className={valueClass}>
            {activeConfig?.[field] ?? fallback ?? "-"}
          </p>
        )}
      </div>
    );
  };

  const releaseNotesError =
    formik.touched.releaseNotesText && formik.errors.releaseNotesText;

  return (
    <div className="flex min-h-full w-full min-w-0 flex-col bg-white p-4 sm:h-full sm:min-h-0 sm:overflow-hidden">
      {(loading || saving) && <Spinner />}

      {/* Title and platform tabs stay put; only the form fields scroll, with the buttons pinned below them. */}
      <div className="flex shrink-0 items-center justify-between gap-3">
        <div>
          <span
            className={`page-title ${langMode === "ar" ? "text-sm" : "text-md"} ${themeClass}`}
          >
            {translations[langMode].appUpdateConfigTitle}
          </span>
        </div>
        {!isEditing && canUpdate && (
          <CommonButton colorIntent="primary"
            onClick={handleEdit}
            variant="primary"
            type="button"
            tooltipText={translations[langMode].editAppUpdateConfigTooltip}
            tooltipPosition="left"
          >
            <Pencil size={16} />
            {translations[langMode].edit}
          </CommonButton>
        )}
      </div>

      <div className="mt-4 flex w-full max-w-[320px] shrink-0 rounded-lg border border-gray-200 bg-gray-50 p-1">
        {PLATFORMS.map((platform) => {
          const isTabDisabled = platform.disabled || isEditing;
          const tooltip = isEditing
            ? translations[langMode].switchPlatformDisabledTooltip
            : platform.disabled
              ? translations[langMode].iosConfigComingSoonTooltip
              : "";
          return (
            <div key={platform.key} className="group relative flex-1">
              <HoverTooltip text={tooltip} position="right" wrapperClassName="flex w-full">
              <button
                type="button"
                disabled={isTabDisabled}
                onClick={() => handleTabChange(platform.key)}
                className={`w-full rounded-md px-4 py-2 text-sm font-medium transition ${
                  isTabDisabled ? "cursor-not-allowed opacity-60" : ""
                } ${
                  activePlatform === platform.key
                    ? "bg-white text-blue-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {platform.label}
              </button>
              </HoverTooltip>
            </div>
          );
        })}
      </div>

      <form
        onSubmit={formik.handleSubmit}
        className={`mt-4 flex min-h-0 flex-1 flex-col overflow-hidden rounded-[16px] border border-gray-200 bg-white ${themeClass}`}
      >
        <div className="grid min-h-0 flex-1 grid-cols-1 content-start gap-4 overflow-y-auto p-4 sm:grid-cols-2 sm:p-6 xl:grid-cols-3">
          {renderField(translations[langMode].appUpdatePlatform, "platform", {
            readOnly: true,
            fallback: activePlatform,
          })}
          {renderField(
            translations[langMode].appUpdateLatestVersion,
            "latestVersion",
            { required: true }
          )}
          {renderField(
            translations[langMode].appUpdateLatestBuildNumber,
            "latestBuildNumber",
            { type: "number", required: true }
          )}
          {renderField(
            translations[langMode].appUpdateMinBuildNumber,
            "minimumSupportedBuildNumber",
            { type: "number", required: true }
          )}
          {renderField(translations[langMode].appUpdateTitleLabel, "title", {
            required: true,
          })}
          {renderField(translations[langMode].appUpdateMessageLabel, "message", {
            required: true,
          })}

          {/* App URL – spans 2 columns, Force Update shares the row */}
          <div className="flex flex-col gap-2 sm:col-span-2 xl:col-span-2">
            <label className={labelClass}>
              <span>{translations[langMode].appUpdateUrlLabel}</span>
              {isEditing && <span className="text-[#FF9F29]">*</span>}
            </label>
            {isEditing ? (
              <>
                <input
                  type="text"
                  name="updateUrl"
                  className={`${inputClass} ${
                    formik.touched.updateUrl && formik.errors.updateUrl
                      ? "border-red-500"
                      : ""
                  }`}
                  value={formik.values.updateUrl ?? ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.updateUrl && formik.errors.updateUrl && (
                  <p className="text-xs text-red-500">
                    {formik.errors.updateUrl}
                  </p>
                )}
              </>
            ) : (
              <p className={valueClass}>{activeConfig?.updateUrl || "-"}</p>
            )}
          </div>

          {/* Force update – checkbox */}
          <div className="flex flex-col gap-2">
            <label className={labelClass}>
              <span>{translations[langMode].appUpdateForceUpdate}</span>
            </label>
            <label className="flex min-h-[46px] items-center gap-2 text-sm text-gray-900">
              <input
                type="checkbox"
                name="forceUpdate"
                readOnly={!isEditing}
                tabIndex={isEditing ? undefined : -1}
                className={`h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                  isEditing ? "" : "pointer-events-none"
                }`}
                checked={
                  isEditing
                    ? formik.values.forceUpdate
                    : !!activeConfig?.forceUpdate
                }
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <span>{translations[langMode].appUpdateForceUpdate}</span>
            </label>
          </div>

          {/* Release notes – full width */}
          <div className="flex flex-col gap-2 sm:col-span-2 xl:col-span-3">
            <label className={labelClass}>
              <span>{translations[langMode].appUpdateReleaseNotes}</span>
              {isEditing && <span className="text-[#FF9F29]">*</span>}
            </label>
            {isEditing ? (
              <>
                <textarea
                  rows={4}
                  name="releaseNotesText"
                  className={`${inputClass} ${
                    releaseNotesError ? "border-red-500" : ""
                  }`}
                  placeholder={
                    translations[langMode].appUpdateReleaseNotesPlaceholder
                  }
                  value={formik.values.releaseNotesText}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {releaseNotesError && (
                  <p className="text-xs text-red-500">
                    {formik.errors.releaseNotesText}
                  </p>
                )}
              </>
            ) : activeConfig?.releaseNotes?.length ? (
              <ul className="list-disc space-y-1 rounded-md border border-[#E5E7EB] bg-gray-50 py-3 pl-8 pr-3 text-sm text-gray-900">
                {activeConfig.releaseNotes.map((note, index) => (
                  <li key={index}>{note}</li>
                ))}
              </ul>
            ) : (
              <p className={valueClass}>-</p>
            )}
          </div>

          <div className="flex flex-col gap-2 sm:col-span-2 xl:col-span-3">
            <label className={labelClass}>
              <span>{translations[langMode].appUpdateUpdatedAt}</span>
            </label>
            <p className={valueClass}>{formatDate(activeConfig?.updatedAt)}</p>
          </div>
        </div>

        {isEditing && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-[16px] border-t border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4">
            <CommonButton colorIntent="danger"
              onClick={handleCancel}
              variant="danger"
              type="button"
              disabled={saving}
            >
              {translations[langMode].cancel}
            </CommonButton>
            <CommonButton colorIntent="success"
              variant="primary"
              type="submit"
              disabled={saving}
            >
              {saving
                ? translations[langMode].Saving
                : translations[langMode].update}
            </CommonButton>
          </div>
        )}
      </form>
    </div>
  );
};

export default AppUpdateConfig;
